import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRightLeft,
  Ban,
  Building2,
  Check,
  Database,
  ExternalLink,
  Image as ImageIcon,
  Loader2,
  Lock,
  Mail,
  MapPin,
  Pencil,
  Phone,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { formatMelbourneDateTime } from "../../../utils/melbourneTime";
import AgentNotesSummary from "../../../components/notes/AgentNotesSummary";
import { MarketStatusLine } from "../../database/components/MarketStatus";
import {
  getPublicPropertyProfile,
  getProspectAppraisalPrefill,
  updateCustomColumn,
} from "../../../api/database";

// The Public Property page - one property in the Database, as a property page
// in the CRM. Christian Gravias, CRM-2026-00031 (2026-09-07): "Please also
// create the property profile to sit in our CRM."
//
// 2026-09-16, same ticket: "I also want all the pages to open up like the other
// property pages below. It all needs to be the same format." So this page is
// laid out exactly like PropertyDetailPage - the same header, the same tab bar,
// the same two-column Property tab with Property Information, Listing Details
// and Location on the right. Only the accent stays indigo, because the layer
// itself is still meant to read as one below appraisals and listings ("another
// colour that is a layer lower", 7 Sep) and the search bar marks it the same way.
//
// The Database row stays the source of truth, so this page reads and does not
// write: editing happens on the row (Open in Database), and "Convert to
// appraisal" is the one door up into the appraisal/listing layers. See
// prospecting/services/public_properties.py.

const TEMP_CHIP = {
  hot: "bg-rose-500 shadow-rose-100",
  warm: "bg-amber-500 shadow-amber-100",
  cold: "bg-sky-500 shadow-sky-100",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// `last_sold_date` and `next_call_at` are plain DATE columns (2013-01-25), so
// they are formatted straight off the string. Reading them through `new Date()`
// puts them at UTC midnight and a Melbourne viewer sees the day before - the
// same reason ProspectDrawer formats them this way.
const dateAU = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}` : "";
};

const dollars = new Intl.NumberFormat("en-AU", { maximumFractionDigits: 0 });

// Land size arrives from a DecimalField, so "640.00". Number() drops the scale
// nobody measured to.
const sqm = (value) => (value ? `${Number(value)}` : "");

const money = (value) => {
  const n = Number(value);
  if (!value || Number.isNaN(n)) return "";
  return `$${dollars.format(n)}`;
};

// The tab bar. Michael Piccoli, 17 Sep 2026: "I also think Christian wants more
// navigation tabs for each of the properties, how can we add more or eventually
// have the ability to add more/convert them to a regular property down the
// track?"
//
// So the bar now carries the same tabs a listing has, in the same order, and is
// honest about which ones a Database row can fill. Property, Owner, Database and
// Notes read the row. The rest exist only once the property is a real CRM
// property - there is no gallery, no open home, no enquiry and no campaign on a
// farm-area address - so they are shown locked, and opening one says what it
// would hold and offers the one door up: Convert to appraisal. That is the
// "convert them to a regular property down the track" path, and it is the same
// path the Database grid uses.
const TABS = [
  { id: "property", label: "Property" },
  { id: "owner", label: "Owner" },
  { id: "database", label: "Database" },
  { id: "notes", label: "Agent Notes" },
  {
    id: "photos",
    label: "Photos",
    locked: "Photos are uploaded against a real property record. Convert this to an appraisal and the gallery, floorplans and video all come with it.",
  },
  {
    id: "open_homes",
    label: "Open Homes",
    locked: "Open homes and inspections are run off a listing. Once converted, every check-in on this address is logged here.",
  },
  {
    id: "enquiries",
    label: "Enquiries / Buyers",
    locked: "Enquiries, attendees, buyers and offers arrive through the portal and the portals feed. They attach to a listing, not to a Database row.",
  },
  {
    id: "analytics",
    label: "Analytics",
    locked: "Campaign analytics count views, enquiries and inspection attendance on a live campaign. A Database row has no campaign yet.",
  },
  {
    id: "documents",
    label: "Documents",
    locked: "Authorities, contracts and e-signed documents sit on the property they belong to. Convert first and they are stored here.",
  },
  {
    id: "buyer_match",
    label: "Buyer Match",
    locked: "Buyer Match shares a property with buyers whose saved search fits it. It runs on appraisals and listings, so it stays off this layer until you convert.",
  },
];

export default function DatabasePropertyProfilePage() {
  const { recordId } = useParams();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [converting, setConverting] = useState(false);
  const [activeTab, setActiveTab] = useState("property");

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getPublicPropertyProfile(recordId);
      setProfile(res.data);
    } catch (e) {
      // A row outside the caller's read scope answers 404 on purpose: whether a
      // colleague's private Database holds a given address is itself private.
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error("Could not load this property profile");
    } finally {
      setIsLoading(false);
    }
  }, [recordId]);

  useEffect(() => {
    load();
  }, [load]);

  // Every agent who can open this profile can appraise the address. The
  // appraisal is their own new property record, so converting writes nothing
  // back to the Database the address was read out of: a view-only reader gets
  // the appraisal and the row stays in the owner's grid. Only the owner's own
  // conversion retires the row. "Add a button for agents to search the
  // property profile and turn it into an appraisal" - Christian Gravias,
  // CRM-2026-00031, 21 Sep 2026, looking at a profile in a Database shared
  // with him read only, where this button used to be replaced by a badge.
  const handleConvert = async () => {
    setConverting(true);
    try {
      const res = await getProspectAppraisalPrefill(recordId);
      navigate("/properties/add-property", {
        state: {
          listingType: "appraisal",
          prospectRecordId: Number(recordId),
          prefill: res.data?.prefill || {},
          retiresRecord: res.data?.retires_record !== false,
          sourceOwnerName: profile?.agent_name || "",
        },
      });
    } catch {
      toast.error("Couldn't open the appraisal form. Please try again.");
    } finally {
      setConverting(false);
    }
  };

  // Naming a column writes the label onto the Database's own column definition,
  // so it reads properly here, in the grid and anywhere else the column is
  // shown - the point being that nobody has to work out what `column_6` was
  // twice. Only the agent who owns the Database can do it; a shared reader gets
  // no pencil (the endpoint is scoped to request.user either way).
  const handleRename = async (column, label) => {
    const clean = String(label || "").trim();
    if (!clean || !column.column_id) return;
    try {
      await updateCustomColumn(column.column_id, { label: clean });
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              database_columns: (prev.database_columns || []).map((c) =>
                c.key === column.key ? { ...c, label: clean, unnamed: false } : c
              ),
            }
          : prev
      );
      toast.success(`Column named "${clean}"`);
    } catch {
      toast.error("Could not save that column name. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-indigo-400" />
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <Building2 className="w-8 h-8 text-gray-300" />
        </div>
        <h3 className="text-lg font-bold text-gray-400">Property profile not found</h3>
        <p className="text-sm text-gray-300 mt-1 max-w-md">
          It may have been converted to an appraisal, deleted from the Database, or it sits in a
          Database that has not been shared with you.
        </p>
        <button
          onClick={() => navigate(-1)}
          className="mt-5 px-4 py-2 rounded-xl bg-indigo-500 text-white text-xs font-bold"
        >
          Go back
        </button>
      </div>
    );
  }

  const people = profile.people || [];
  const notes = profile.notes || [];
  const sellerLeads = profile.seller_leads || [];
  const blocked = profile.unsubscribed_names || [];
  const location = [profile.suburb, profile.state, profile.postcode].filter(Boolean).join(" ");
  // The Database stores the suburb, state and postcode in the address for most
  // rows, so the second line under Location is only worth printing when it
  // actually adds something rather than repeating the line above it.
  const fullAddress =
    location && !(profile.address || "").toLowerCase().includes(location.toLowerCase())
      ? `${profile.address}, ${location}`
      : "";
  // The row's extra spreadsheet columns, named and ordered the way the Database
  // grid shows them. Michael Piccoli, 17 Sep 2026: "Data isn't feeding properly
  // Eg. We should know what each of these columns/rows are supposed to be" -
  // this page used to print the storage keys (`column_3`, `column_6`) straight
  // out of custom_fields. The backend resolves the agent's own column labels,
  // renames, order and hidden columns now, and flags the ones the uploaded
  // spreadsheet left with no heading at all.
  //
  // The fallback keeps an older payload readable rather than blank.
  const columns =
    profile.database_columns ||
    Object.entries(profile.custom_fields || {})
      .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== "")
      .map(([key, value]) => ({ key, label: key, value, type: "text", unnamed: false }));
  const named = columns.filter((c) => !c.unnamed);
  const unnamed = columns.filter((c) => c.unnamed);
  const upload = profile.source_upload;
  const lockedTab = TABS.find((t) => t.id === activeTab && t.locked);

  return (
    <div className="flex-1 flex flex-col bg-[#F8FAFC] min-h-screen -mx-[14px] sm:-mx-4 md:-m-6">
      {/* Header - the same shape as a listing's: status badge, type, address,
          then the row of actions. */}
      <div className="bg-white border-b border-gray-100 md:sticky md:top-0 md:z-40 shadow-sm">
        <div className="w-full px-4 sm:px-6 md:px-8 py-4 sm:py-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="w-10 h-10 flex-shrink-0 bg-white border border-gray-200 rounded-xl flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm"
              >
                <ArrowLeft className="w-5 h-5 text-[#374151]" />
              </button>
              <div className="flex flex-col space-y-2 min-w-0 flex-1">
                <div className="flex items-center space-x-3 flex-wrap gap-1">
                  <span
                    title="A property profile built from a Database row. One layer below your appraisals and listings."
                    className="inline-flex items-center px-2 py-0.5 bg-indigo-500 text-white text-[9px] font-black rounded uppercase tracking-[0.2em] leading-none shadow-lg shadow-indigo-100 flex-shrink-0"
                  >
                    Public Property
                  </span>
                  {profile.lead_temp && (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 text-white text-[9px] font-black rounded uppercase tracking-[0.2em] leading-none shadow-lg flex-shrink-0 ${
                        TEMP_CHIP[profile.lead_temp] || "bg-gray-400 shadow-gray-100"
                      }`}
                    >
                      {profile.lead_temp}
                    </span>
                  )}
                  <span className="text-[10px] text-[#374151] opacity-70 font-bold uppercase tracking-widest flex items-center">
                    <Building2 className="w-3 h-3 mr-1" />
                    {profile.property_type || "Residential"}
                  </span>
                </div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-black text-[#374151] tracking-tighter leading-tight break-words">
                  {profile.address}
                </h1>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-2 sm:gap-4 sm:flex-shrink-0">
              <RouterLink
                to={`/database?record=${profile.record_id}`}
                title="Open the Database row this profile is built from"
                className="flex items-center justify-center space-x-2 px-3 sm:px-6 py-2.5 sm:py-3 bg-[#2D3139] hover:bg-black text-white rounded-xl text-[10px] font-black transition-all shadow-xl shadow-black/5 active:scale-95 uppercase tracking-widest sm:flex-none min-w-0"
              >
                <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">OPEN IN DATABASE</span>
              </RouterLink>
              <button
                onClick={handleConvert}
                disabled={converting}
                title={
                  profile.can_edit
                    ? "Opens the Appraisal form with this property address filled in"
                    : `Opens the Appraisal form with this property address filled in. The row stays in ${profile.agent_name || "the owner"}'s Database.`
                }
                className="flex items-center justify-center space-x-2 px-3 sm:px-6 py-2.5 sm:py-3 rounded-xl text-[10px] font-black transition-all shadow-sm active:scale-95 uppercase tracking-widest border sm:flex-none min-w-0 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 border-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {converting ? (
                  <Loader2 className="w-3.5 h-3.5 flex-shrink-0 animate-spin" />
                ) : (
                  <ArrowRightLeft className="w-3.5 h-3.5 flex-shrink-0" />
                )}
                <span className="truncate">CONVERT TO APPRAISAL</span>
              </button>
              {/* Still true, and still worth saying - the ROW can't be edited
                  from here. It just no longer stands in for the button. */}
              {!profile.can_edit && (
                <span
                  title={`This Database belongs to ${profile.agent_name || "another agent"}. You can read the row and appraise the address; editing the row is theirs.`}
                  className="col-span-2 inline-flex items-center gap-1.5 px-3 sm:px-5 py-2.5 sm:py-3 rounded-xl text-[10px] font-black uppercase tracking-widest bg-gray-100 text-gray-500 border border-gray-200"
                >
                  <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">View Only — Shared Database</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1">
        <div className="px-4 sm:px-6 md:px-8 border-b border-gray-100 bg-white">
          <nav className="flex space-x-6 sm:space-x-10 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                title={tab.locked || undefined}
                className={`group py-6 px-1 border-b-4 font-black text-xs tracking-[0.15em] transition-all flex items-center gap-1.5 uppercase whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-indigo-500 text-indigo-500"
                    : tab.locked
                      ? "border-transparent text-[#374151] opacity-30 hover:opacity-60"
                      : "border-transparent text-[#374151] opacity-50 hover:opacity-100"
                }`}
              >
                {tab.locked && <Lock className="w-3 h-3" />}
                {tab.label}
                {tab.id === "notes" && notes.length > 0 && ` (${notes.length})`}
                {tab.id === "owner" && people.length > 1 && ` (${people.length})`}
                {tab.id === "database" && columns.length > 0 && ` (${columns.length})`}
              </button>
            ))}
          </nav>
        </div>

        <div className="pb-20 px-4 sm:px-6 md:px-8 pt-6 space-y-6">
          {/* Compliance first, on every tab: an unsubscribed owner must not be
              emailed or texted off the back of this page. */}
          {blocked.length > 0 && (
            <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
              <Ban size={15} className="text-rose-600 mt-0.5 shrink-0" />
              <div className="text-[12.5px] text-rose-800">
                <b>Do not contact: {blocked.join(", ")}.</b>{" "}
                {blocked.length === people.length
                  ? "Excluded from every email and SMS."
                  : "The other owner on this property can still be reached."}
              </div>
            </div>
          )}

          {activeTab === "property" && (
            <div className="space-y-6 animate-in fade-in duration-500">
              {/* Where this page sits, said plainly - the client's whole point
                  was that this layer must not read as an appraisal or listing. */}
              <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 px-4 py-3 text-[12.5px] text-indigo-900">
                A <b>Public Property</b> profile, built from
                {profile.can_edit ? " your Database" : ` ${profile.agent_name || "another agent"}'s Database`}. It is one
                layer below your appraisals and listings, so it never appears in My Listings, the
                pipelines or on the portal. Convert it to an appraisal to move it up.
                {!profile.can_edit &&
                  " The appraisal is yours; the row stays in their Database."}
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <div className="xl:col-span-2 space-y-6">
                  <Panel title="Property Images">
                    <div className="flex flex-col items-center justify-center py-14 text-center">
                      <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center mb-3">
                        <ImageIcon className="w-6 h-6 text-gray-300" />
                      </div>
                      <p className="text-[13px] font-bold text-[#374151] opacity-70">No photos yet</p>
                      <p className="text-[11px] text-[#374151] opacity-50 mt-1 max-w-sm leading-relaxed">
                        A Database property carries no gallery. Photos are added once it becomes an
                        appraisal or a listing.
                      </p>
                    </div>
                  </Panel>

                  <Panel title="About the Property">
                    <p className="text-[#374151] text-[13px] leading-[1.8] font-medium opacity-90">
                      {describe(profile)}
                    </p>
                    {named.length > 0 && (
                      <div className="mt-8 space-y-5">
                        <h4 className="font-sans font-medium text-[12.93px] text-[#374151]">
                          From the Database
                        </h4>
                        {named.map((column) => (
                          <Row
                            key={column.key}
                            label={column.label}
                            value={columnValue(column)}
                          />
                        ))}
                      </div>
                    )}
                    {/* A column the spreadsheet left with no heading is not
                        printed here as if it were a fact about the property.
                        It is named on the Database tab, where the other rows'
                        values for the same column are shown alongside it. */}
                    {unnamed.length > 0 && (
                      <button
                        onClick={() => setActiveTab("database")}
                        className="mt-8 w-full text-left flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 hover:bg-amber-100 transition-colors"
                      >
                        <Database size={15} className="text-amber-600 mt-0.5 shrink-0" />
                        <span className="text-[12.5px] text-amber-900">
                          <b>
                            {unnamed.length} column{unnamed.length === 1 ? "" : "s"} on this row
                            {unnamed.length === 1 ? " has" : " have"} no heading
                          </b>{" "}
                          {upload?.filename
                            ? `- the heading cell was empty in ${upload.filename}. `
                            : "- the heading cell was empty in the spreadsheet it came from. "}
                          Open the Database tab to see what {unnamed.length === 1 ? "it holds" : "they hold"} and name{" "}
                          {unnamed.length === 1 ? "it" : "them"}.
                        </span>
                      </button>
                    )}
                  </Panel>
                </div>

                <div className="space-y-6">
                  <Panel title="Property Information">
                    <div className="space-y-5">
                      {[
                        { label: "Class", value: profile.property_type || "Not stated", isBadge: true },
                        { label: "Type", value: "Public Property", isBadge: true },
                        { label: "Bedrooms", value: profile.beds, isNumeric: true },
                        { label: "Bathrooms", value: profile.baths, isNumeric: true },
                        { label: "Garage", value: profile.car, isNumeric: true },
                        { label: "Land Area", value: sqm(profile.land_sqm), isArea: true },
                        {
                          label: "Last Sold",
                          value: [money(profile.last_sold_price), dateAU(profile.last_sold_date)]
                            .filter(Boolean)
                            .join(" · "),
                          isPrice: !!profile.last_sold_price,
                        },
                      ]
                        .filter((item) =>
                          item.isNumeric ? item.value != null : item.isBadge || item.value
                        )
                        .map((item) => (
                          <Row key={item.label} {...item} />
                        ))}
                    </div>
                  </Panel>

                  <Panel title="Record Details">
                    <div className="space-y-5">
                      <Row label="Record ID" value={String(profile.record_id)} />
                      <Row label="Category" value={profile.category || "All"} />
                      <Row label="Database" value={profile.agent_name || "-"} />
                      <Row label="Added" value={formatMelbourneDateTime(profile.created_at) || "-"} />
                    </div>
                  </Panel>

                  <Panel title="Selling Intent">
                    <div className="space-y-5">
                      <div>
                        <Row label="Looking to sell" value={profile.timeframe || "Not stated"} />
                        <MarketStatusLine op={profile} />
                      </div>
                      <Row label="Next call" value={dateAU(profile.next_call_at) || "None set"} />
                      <Row
                        label="Last contacted"
                        value={formatMelbourneDateTime(profile.last_contacted_at) || "Never"}
                      />
                    </div>
                  </Panel>

                  {/* Seller leads on the Prospects board at this address
                      (Christian Gravias, DOMA 13321183498, 30 Oct 2026). */}
                  {sellerLeads.length > 0 && (
                    <Panel title={`Seller Leads (${sellerLeads.length})`}>
                      <div className="space-y-5">
                        {sellerLeads.map((lead) => (
                          <div key={lead.id}>
                            <Row label={lead.name || "Unnamed"} value={lead.stage_label || "New"} />
                            <p className="mt-1 text-[11px] text-[#374151] opacity-60">
                              {[lead.agent_name, ...(lead.phones || [])].filter(Boolean).join(" · ")}
                            </p>
                            <RouterLink
                              to={lead.url}
                              className="inline-block mt-2 text-[11px] font-bold text-indigo-600 hover:underline"
                            >
                              Open in Prospects
                            </RouterLink>
                          </div>
                        ))}
                      </div>
                    </Panel>
                  )}

                  <Panel title="Location">
                    <div className="flex items-start space-x-3">
                      <div className="mt-0.5">
                        <MapPin className="w-4 h-4 text-[#374151] opacity-40" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-bold text-[#374151]">
                          {profile.address || "Address not available"}
                        </p>
                        {fullAddress && (
                          <p className="text-[11px] text-[#374151] opacity-70 font-normal leading-relaxed">
                            {fullAddress}
                          </p>
                        )}
                      </div>
                    </div>
                    {profile.address && (
                      <div className="mt-6 rounded-[12px] overflow-hidden border border-gray-200 shadow-sm h-[260px]">
                        <iframe
                          title="Property location map"
                          width="100%"
                          height="100%"
                          style={{ border: 0 }}
                          loading="lazy"
                          allowFullScreen
                          referrerPolicy="no-referrer-when-downgrade"
                          src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(
                            `${profile.address} Australia`
                          )}`}
                        />
                      </div>
                    )}
                  </Panel>
                </div>
              </div>
            </div>
          )}

          {activeTab === "owner" && (
            <div className="animate-in fade-in duration-500">
              <Panel title={people.length > 1 ? `Property Owners (${people.length})` : "Property Owner"}>
                {people.length === 0 ? (
                  <p className="text-sm text-gray-400">No owner details on this row yet.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {people.map((p, i) => (
                      <div key={i} className="rounded-xl border border-gray-100 p-5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-bold text-[#374151] text-sm truncate">
                              {p.name || "Unnamed"}
                            </p>
                            <p className="text-[10px] text-[#374151] opacity-50 uppercase tracking-widest font-bold mt-0.5">
                              {people.length > 1 ? `Owner ${i + 1}` : "Owner"}
                            </p>
                          </div>
                          {p.unsubscribed && (
                            <span className="px-2 py-0.5 rounded bg-rose-50 text-rose-600 text-[10px] font-bold uppercase tracking-wider shrink-0">
                              Do not contact
                            </span>
                          )}
                        </div>
                        <div className="mt-3 space-y-1.5 text-[12.5px] text-[#374151] opacity-80">
                          {p.mobile && (
                            <p className="flex items-center gap-1.5 truncate">
                              <Phone className="w-3.5 h-3.5 text-gray-300" /> {p.mobile}
                            </p>
                          )}
                          {p.email && (
                            <p className="flex items-center gap-1.5 truncate">
                              <Mail className="w-3.5 h-3.5 text-gray-300" /> {p.email}
                            </p>
                          )}
                          {!p.mobile && !p.email && (
                            <p className="text-gray-300">No phone or email on this row.</p>
                          )}
                        </div>
                        {/* Two owners on one property are two contacts, each
                            with their own digital profile. */}
                        {p.contact_id && (
                          <RouterLink
                            to={`/contacts/${p.contact_id}`}
                            className="inline-block mt-3 text-[11px] font-bold text-indigo-600 hover:underline"
                          >
                            Open contact profile
                          </RouterLink>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
            </div>
          )}

          {activeTab === "database" && (
            <div className="space-y-6 animate-in fade-in duration-500">
              {/* Where the row came from. "We should know what each of these
                  columns/rows are supposed to be" starts with which spreadsheet
                  it was, on which day, into which category. */}
              <Panel title="Where this row came from">
                <div className="space-y-5">
                  <Row label="Database" value={profile.agent_name || "-"} />
                  <Row label="Category" value={profile.category || "All"} />
                  <Row label="Record ID" value={String(profile.record_id)} />
                  {upload?.filename && <Row label="Uploaded file" value={upload.filename} />}
                  {upload?.uploaded_at && (
                    <Row label="Uploaded" value={formatMelbourneDateTime(upload.uploaded_at) || "-"} />
                  )}
                  {upload?.row_count ? (
                    <Row label="Rows in that upload" value={String(upload.row_count)} />
                  ) : null}
                  <Row label="Added to the CRM" value={formatMelbourneDateTime(profile.created_at) || "-"} />
                </div>
                {!upload && (
                  <p className="mt-6 text-[11.5px] text-[#374151] opacity-60 leading-relaxed">
                    This row was not created by a CSV upload, or the upload record has since been
                    removed, so there is no source file to name.
                  </p>
                )}
              </Panel>

              <Panel title={`Spreadsheet Columns (${columns.length})`}>
                {columns.length === 0 ? (
                  <p className="text-sm text-gray-400">
                    This row carries no extra columns beyond the property and owner fields above.
                  </p>
                ) : (
                  <>
                    <p className="-mt-4 mb-6 text-[11.5px] text-[#374151] opacity-60 leading-relaxed">
                      Every extra column on this row, named the way it is named in the Database.
                      Rename one and it reads the same way in the grid and on every property in
                      this Database.
                    </p>
                    <div className="divide-y divide-gray-100">
                      {columns.map((column) => (
                        <ColumnRow
                          key={column.key}
                          column={column}
                          onRename={handleRename}
                        />
                      ))}
                    </div>
                  </>
                )}
              </Panel>
            </div>
          )}

          {/* A tab a Database row cannot fill. It says what it would hold and
              offers the way to get there, rather than opening an empty page. */}
          {lockedTab && (
            <div className="animate-in fade-in duration-500">
              <Panel title={lockedTab.label}>
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mb-4">
                    <Lock className="w-6 h-6 text-indigo-300" />
                  </div>
                  <p className="text-[13px] font-bold text-[#374151]">
                    {lockedTab.label} opens once this is a regular property
                  </p>
                  <p className="text-[12px] text-[#374151] opacity-60 mt-2 max-w-lg leading-relaxed">
                    {lockedTab.locked}
                  </p>
                  <button
                    onClick={handleConvert}
                    disabled={converting}
                    className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
                  >
                    {converting ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <ArrowRightLeft className="w-3.5 h-3.5" />
                    )}
                    Convert to a regular property
                  </button>
                  <p className="text-[11px] text-[#374151] opacity-50 mt-3 max-w-md leading-relaxed">
                    Converting opens the Appraisal form with this address, owner and property
                    details already filled in.{" "}
                    {profile.can_edit
                      ? "The Database row leaves the grid and links to the new property."
                      : `The appraisal is yours. The Database row stays in ${profile.agent_name || "the owner"}'s grid, untouched.`}
                  </p>
                </div>
              </Panel>
            </div>
          )}

          {activeTab === "notes" && (
            <div className="animate-in fade-in duration-500">
              <Panel title={`Agent Notes (${notes.length})`}>
                <AgentNotesSummary notes={notes} subject={profile.address} className="mb-4" />
                {notes.length === 0 ? (
                  <p className="text-sm text-gray-400">
                    No notes yet. Notes are logged against the row in the Database.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {notes.map((n) => (
                      <div key={n.id} className="rounded-xl bg-gray-50 px-4 py-3">
                        <p className="text-[13px] text-[#374151] whitespace-pre-wrap">{n.body}</p>
                        <p className="text-[11px] text-[#374151] opacity-50 mt-1.5">
                          {n.author_name || "Unknown"} · {formatMelbourneDateTime(n.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// A listing's description is written by the agent. A Database row has none, so
// the facts on the row are read back as one sentence rather than leaving the
// panel that every other property page fills sitting empty.
function describe(profile) {
  const bits = [];
  if (profile.property_type) bits.push(`a ${String(profile.property_type).toLowerCase()}`);
  if (profile.beds != null) bits.push(`${profile.beds} bed`);
  if (profile.baths != null) bits.push(`${profile.baths} bath`);
  if (profile.car != null) bits.push(`${profile.car} car`);
  if (profile.land_sqm) bits.push(`${Number(profile.land_sqm)} m² of land`);

  const where = [profile.suburb, profile.state, profile.postcode].filter(Boolean).join(" ");
  const opening = bits.length
    ? `${profile.address} is ${bits.join(", ")}`
    : `${profile.address}`;
  const sold =
    profile.last_sold_price || profile.last_sold_date
      ? ` Last recorded sale: ${[money(profile.last_sold_price), dateAU(profile.last_sold_date)]
          .filter(Boolean)
          .join(" in ")}.`
      : " No sale recorded in the Database.";

  return `${opening}${where ? `, in ${where}` : ""}.${sold}`;
}

// A column value read back the way its column type says it should be. The
// Database grid formats money and dates; a raw "750000" on a Money column read
// as a mystery number here.
function columnValue(column) {
  const raw = column.value;
  if (raw === null || raw === undefined || String(raw).trim() === "") return "";
  const text = String(raw).trim();
  if (column.type === "money") {
    const n = Number(text.replace(/[$,\s]/g, ""));
    return Number.isNaN(n) ? text : money(n);
  }
  if (column.type === "date") {
    return dateAU(text) || text;
  }
  if (column.type === "number") {
    const n = Number(text.replace(/,/g, ""));
    return Number.isNaN(n) ? text : dollars.format(n);
  }
  return text;
}

// One row on the Database tab. A named column is a plain label and value. An
// unnamed one - the spreadsheet's heading cell was empty, so `column_6` is all
// anybody ever knew about it - shows what other rows in the same upload hold in
// it, offers a cautious suggestion, and takes a name.
function ColumnRow({ column, onRename }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const start = () => {
    setDraft(column.unnamed ? column.suggested_label || "" : column.label);
    setEditing(true);
  };

  const save = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    await onRename(column, draft);
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="py-4 first:pt-0 last:pb-0">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") save();
                  if (e.key === "Escape") setEditing(false);
                }}
                placeholder="What is this column?"
                className="px-2.5 py-1.5 rounded-lg border border-indigo-200 text-[12.5px] focus:outline-none focus:ring-1 focus:ring-indigo-400 w-56"
              />
              <button
                onClick={save}
                disabled={saving || !draft.trim()}
                title="Save this column name"
                className="p-1.5 rounded-lg bg-indigo-500 text-white disabled:opacity-40"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={() => setEditing(false)}
                title="Cancel"
                className="p-1.5 rounded-lg bg-gray-100 text-gray-500"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`font-sans text-[11.5px] leading-[17.24px] ${
                  column.unnamed
                    ? "text-amber-700 font-bold uppercase tracking-wider"
                    : "text-[#374151] opacity-70"
                }`}
              >
                {column.unnamed
                  ? `Unnamed column${column.position ? ` (column ${column.position} of the file)` : ""}`
                  : column.label}
              </span>
              {column.can_rename && (
                <button
                  onClick={start}
                  title={column.unnamed ? "Name this column" : "Rename this column"}
                  className="text-indigo-500 hover:text-indigo-700"
                >
                  <Pencil className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        </div>
        <span className="font-sans font-bold text-[12.93px] leading-[18.75px] text-[#374151] text-right break-words max-w-[55%]">
          {columnValue(column) || "-"}
        </span>
      </div>

      {column.unnamed && (
        <div className="mt-2.5 space-y-1.5">
          {column.suggested_label && (
            <p className="text-[11px] text-[#374151] opacity-70">
              Looks like a <b>{column.suggested_label.toLowerCase()}</b> column
              {column.can_rename ? " - name it to confirm." : "."}
            </p>
          )}
          {column.examples?.length > 0 && (
            <p className="text-[11px] text-[#374151] opacity-50 break-words">
              Other rows here hold: {column.examples.map((v) => `"${v}"`).join(", ")}
            </p>
          )}
          {!column.can_rename && (
            <p className="text-[11px] text-[#374151] opacity-50">
              Only the agent who owns this Database can name it.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6 sm:p-8 shadow-sm">
      <h3 className="font-sans font-medium text-[12.93px] leading-[18.75px] text-[#374151] mb-8">
        {title}
      </h3>
      {children}
    </div>
  );
}

function Row({ label, value, isBadge, isPrice, isArea, isNumeric }) {
  const shown = isNumeric ? (value != null ? value : "-") : value || "-";
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="font-sans font-normal text-[11.5px] leading-[17.24px] text-[#374151] opacity-70">
        {label}
      </span>
      {isBadge ? (
        <span className="bg-indigo-500/10 text-indigo-600 uppercase text-[10px] font-bold tracking-wider px-2 py-0.5 rounded">
          {shown}
        </span>
      ) : isPrice ? (
        <span className="font-sans font-bold text-[12.93px] leading-[18.75px] text-indigo-600 text-right">
          {shown}
        </span>
      ) : (
        <span className="font-sans font-bold text-[12.93px] leading-[18.75px] text-[#374151] text-right break-words">
          {shown}
          {isArea && shown !== "-" ? "m²" : ""}
        </span>
      )}
    </div>
  );
}
